import { useContext } from "react";
import { ApolloError } from "@apollo/client";
import { UiSchema } from "@rjsf/utils";
import { get, some, values } from "lodash";
import { useLocation } from "react-router";
import { SUBMISSION_CATEGORY } from "common/constants";
import { buildLink } from "common/routing";
import { getDocumentFieldsFromUISchema } from "common/services/submissionHelpers";
import { formatCoordinates } from "common/utils/coordinates";
import { SUBMISSION_CATEGORY_TO_LABEL } from "common/utils/submissions";
import {
  DefaultedValuesArgs,
  useCommonDefaultedValues,
} from "common-client/utils/submissions";
import { AccountDocumentType } from "../../generated/graphql";
import { AuthContext } from "../Authorization/AuthContext";
import { ButtonLink } from "../Common/ButtonLink";
import { Tag } from "../Common/Tag";
import { Row } from "../Inputs/__styles__/Wrapper";
import { RegenerateButtonWrapper, TagWrapper } from "./__styles__/Widget";
import { SubmissionFormContext } from "./Form";

export const useDefaultedValues = <
  Keys extends string = string,
  Values extends Record<Keys, any> = Record<Keys, any>,
  Schema extends Record<keyof Values, UiSchema | undefined> = Record<
    keyof Values,
    UiSchema | undefined
  >,
>(
  args: DefaultedValuesArgs<Keys, Values, Schema>,
) => {
  const { editing, defaultValues } = useContext(SubmissionFormContext);
  return useCommonDefaultedValues({
    ...args,
    defaultValues,
    editing,
  });
};

export const generateUpsertErrorMessage = (error: ApolloError) => {
  const message =
    error.message.includes("Invalid PDF file") ||
    error.message.includes("A record with the unique ID already exists.") ||
    error.message.includes(
      "Please contact support@withforerunner.com for assistance.",
    )
      ? error.message
      : "Your submission failed to process. Please try again.";

  return message;
};

export const castEmptyStringToNull = ({
  value,
  required,
}: {
  value: Maybe<string>;
  required: boolean;
}) => {
  // see commit message for why this `if` is necessary
  if (required) {
    return value === "" ? null : value;
  } else {
    return value;
  }
};

export const hasSummary = ({ summaries }: { summaries: Array<unknown> }) => {
  return summaries.length > 0;
};

type GetObjectDisplayProps = {
  property?: {
    fullAddress?: string | null;
    longitude: number;
    latitude: number;
  } | null;
  customMapGeometry?: {
    label: string;
  } | null;
};

export const getObjectDisplay = ({
  property,
  customMapGeometry,
}: GetObjectDisplayProps) => {
  if (property) {
    return property.fullAddress ?? formatCoordinates({ ...property });
  } else if (customMapGeometry) {
    return customMapGeometry.label;
  }
  return undefined;
};

export const useSubmissionPrevLocation = () => {
  const { isPublic } = useContext(AuthContext);
  const location =
    useLocation<Maybe<{ prevLocation?: string; search?: string }>>();
  let prevLocation: string;
  if (location.state?.prevLocation) {
    prevLocation = location.state.prevLocation;
  } else if (isPublic) {
    prevLocation = buildLink("properties");
  } else {
    prevLocation = `${buildLink("map")}${location.state?.search ?? ""}`;
  }

  return prevLocation;
};

export const SUBMISSION_CATEGORY_OPTIONS: Array<{
  value: SUBMISSION_CATEGORY;
  label: string;
}> = Object.entries(SUBMISSION_CATEGORY_TO_LABEL).map(([value, label]) => ({
  value: value as SUBMISSION_CATEGORY,
  label,
}));

export const AIFieldLabelDecorator = ({
  onRegenerate,
  regenerateHidden,
  regenerateDisabled,
  regenerating,
}: {
  onRegenerate?: () => void;
  regenerateHidden: boolean;
  regenerateDisabled: boolean;
  regenerating: boolean;
}) => {
  return (
    <Row>
      <TagWrapper>
        <Tag leftIconName="sparkles" styleVariant="default">
          Generated with AI
        </Tag>
      </TagWrapper>
      {!regenerateHidden && (
        <RegenerateButtonWrapper>
          <ButtonLink
            onClick={() => onRegenerate?.()}
            styleVariant="primary"
            disabled={regenerateDisabled}
            size="xSmall"
          >
            {regenerating ? "Regenerating..." : "Regenerate"}
          </ButtonLink>
        </RegenerateButtonWrapper>
      )}
    </Row>
  );
};

export const useAIField = ({
  id,
  uiSchema,
  schema,
  ...props
}: {
  id: string;
  uiSchema?: { "ui:useGenerativeAI"?: boolean } & Record<string, unknown>;
  schema: { formula?: unknown } & Record<string, unknown>;
  readonly?: boolean;
  disabled?: boolean;
}) => {
  const { editing, regenerateAIFields, loadingFormPaths } = useContext(
    SubmissionFormContext,
  );

  const isGenAIField = !!uiSchema?.["ui:useGenerativeAI"];
  const isFormulaField = !!schema.formula;
  const readonly = props.readonly || props.disabled || isFormulaField;
  const path = getFieldPathFromId(id);

  const regenerating = loadingFormPaths[path] ?? false;
  const disabled = (!editing && isGenAIField) || regenerating;
  const anyFieldLoading = some(values(loadingFormPaths));

  const labelDecorator = isGenAIField ? (
    <AIFieldLabelDecorator
      onRegenerate={() => {
        regenerateAIFields?.([path]);
      }}
      regenerateHidden={!regenerateAIFields || readonly}
      regenerateDisabled={anyFieldLoading}
      regenerating={regenerating}
    />
  ) : null;

  return {
    disabled,
    readonly,
    labelDecorator,
  };
};

export const submissionTypeContainsEcDocumentType = <
  T extends {
    currentVersion: {
      formStructure: {
        schema: any;
        uiSchema: any;
      };
    };
  },
>({
  submissionType,
  accountDocumentTypes = [],
}: {
  submissionType: T;
  accountDocumentTypes?: Pick<AccountDocumentType, "id" | "documentType">[];
}): boolean => {
  const { schema, uiSchema } = submissionType.currentVersion.formStructure;
  const fields = getDocumentFieldsFromUISchema({ schema, uiSchema });

  return fields.some(field => {
    const accountDocumentTypeId = get(
      schema,
      `${field.schemaPath}.items.properties.accountDocumentTypeId.enum`,
    )?.[0];

    return accountDocumentTypes.some(
      adt =>
        adt.id === accountDocumentTypeId &&
        adt.documentType?.name === "Elevation Certificate",
    );
  });
};

export const getFieldPathFromId = (id: string) =>
  id.replace("root_", "").replace(/_/g, ".");
